import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  CircularProgress,
  createFilterOptions,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { createParcelLockersClient } from '../../helpers/client';
import { ImageLoader } from '../imgLoader/imgLoader';
import { NearestParcelLocker, ParcelLockerProvider } from 'orderme-api-integration-client';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { clearDelivery, deliverToParcelLocker } from '../../redux/reducers/pickupReducer';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { DeliveryDetails } from './whereTo';
import { ItemDescription } from './itemDescription';
import { RootState } from '../../redux/store';
import { useGeolocation } from '../../hooks/useGeolocation';

interface ParcelLockerItemProps {
  deliveryDetails: DeliveryDetails;
  onClick: () => void;
  isSelected: boolean;
  provider: ParcelLockerProvider;
}

export function ParcelLockerItem(props: ParcelLockerItemProps) {
  const { deliveryDetails, onClick, isSelected, provider } = props;
  const { t } = useTranslation(['payment']);
  const parcelLockersClient = useMemo(() => createParcelLockersClient(), []);
  const [parcelLockers, setParcelLockers] = useState<NearestParcelLocker[]>();
  const [filteredParcelLockers, setFilteredParcelLockers] = useState<NearestParcelLocker[]>([]);
  const [selectedParcelLocker, setSelectedParcelLocker] = useState<NearestParcelLocker>(null);
  const [cities, setCities] = useState<string[]>();
  const [selectedCity, setSelectedCity] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const onlyPhysicalItems = useAppSelector((state: RootState) => state.shoppingCartState.onlyPhysicalItems);
  const totalPrice = useAppSelector((state: RootState) => state.shoppingCartState.totalPrice);
  const priceUpToFreeDelivery =
    deliveryDetails.freeDeliveryFrom > 0 ? deliveryDetails.freeDeliveryFrom - totalPrice : 0;
  const isEnabled = onlyPhysicalItems && (priceUpToFreeDelivery <= 0 || deliveryDetails.price > 0);
  const description = !onlyPhysicalItems
    ? [t('payment:deliveryToParcelLockerForGoods')]
    : priceUpToFreeDelivery > 0
      ? [
          t('payment:parcelLockerFreeDeliveryFrom', {
            price: deliveryDetails.freeDeliveryFrom.toPrice(),
          }),
          t('payment:priceUpToFreeDelivery', {
            price: priceUpToFreeDelivery.toPrice(),
          }),
        ]
      : [];

  const getParcelLockers = useCallback(() => {
    parcelLockersClient
      .getAll(provider.id)
      .then((response) => {
        var lockers = response.map(
          (locker) =>
            new NearestParcelLocker({
              value: locker,
              distanceInDegrees: undefined,
              distanceInMeters: undefined,
            })
        );

        setParcelLockers(lockers);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [parcelLockersClient, provider.id]);

  const getParcelLockersByLocation = useCallback(
    (longitude: number, latitude: number) => {
      parcelLockersClient
        .getByLocation(provider.id, longitude, latitude)
        .then((response) => {
          console.log(response);
          const filteredByDistance = response.sort((a, b) => a.distanceInMeters - b.distanceInMeters);
          setParcelLockers(filteredByDistance);

          var responseCities = response.map((res) => res.value.city);
          var uniqueCities = responseCities.filter((c, index) => {
            return responseCities.indexOf(c) === index;
          });
          setCities(uniqueCities);
          if (filteredByDistance.length > 0) {
            setSelectedCity(filteredByDistance[0].value.city);
          }
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [parcelLockersClient, provider.id]
  );

  const { requestLocation } = useGeolocation({
    onLocation: getParcelLockersByLocation,
    onPermissionDenied: getParcelLockers,
  });

  useEffect(() => {
    if (!isSelected) {
      setSelectedParcelLocker(null);
      return;
    }

    if (!parcelLockers) {
      setIsLoading(true);
      requestLocation();
    }
  }, [isSelected, parcelLockers, requestLocation]);

  useEffect(() => {
    if (parcelLockers) {
      var cities = parcelLockers.map((res) => res.value.city);
      var uniqueCities = cities
        .filter((c, index) => cities.indexOf(c) === index)
        .sort((a, b) => a.localeCompare(b, 'lt'));
      setCities(uniqueCities);
    }
  }, [parcelLockers]);

  useEffect(() => {
    if (selectedCity) {
      var citySalesPoints = parcelLockers.filter((parcelLocker) => parcelLocker.value.city === selectedCity);
      // If no distance, then sort by name
      if (citySalesPoints.length > 0 && !citySalesPoints[0].distanceInDegrees) {
        citySalesPoints = citySalesPoints.sort((a, b) => a.value.name.localeCompare(b.value.name, 'lt'));
      }

      setFilteredParcelLockers(citySalesPoints);
    } else {
      setFilteredParcelLockers([]);
    }
  }, [parcelLockers, selectedCity]);

  useEffect(() => {
    if (filteredParcelLockers?.length === 1) {
      setSelectedParcelLocker(filteredParcelLockers[0]);
    } else {
      setSelectedParcelLocker(null);
    }
  }, [filteredParcelLockers]);

  useEffect(() => {
    if (selectedParcelLocker && provider) {
      dispatch(
        deliverToParcelLocker({
          provider: provider.id,
          parcelLocker: selectedParcelLocker,
        })
      );
    } else {
      dispatch(clearDelivery());
    }
  }, [selectedParcelLocker, provider, dispatch]);

  const filterOptions = createFilterOptions({
    stringify: (option: NearestParcelLocker) => option.value.name + option.value.address,
  });

  const onAccordionClick = () => {
    if (!isSelected && isEnabled) {
      onClick();
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', my: '20px' }}>
      <Accordion expanded={isSelected} sx={{ backgroundColor: !isEnabled && '#D2CDC3', width: '100%' }}>
        <AccordionSummary
          aria-controls="panel-content"
          id="panel-header"
          onClick={onAccordionClick}
          style={{ cursor: 'pointer' }}
        >
          <Box>
            <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
              {isSelected ? (
                <RadioButtonCheckedIcon color="secondary" />
              ) : (
                <RadioButtonUncheckedIcon sx={{ color: 'rgba(0, 0, 0, 0.54);' }} />
              )}
              <Box display="flex" alignItems="center" sx={{ mr: '10px' }}>
                <ImageLoader sx={{ height: '50px', marginLeft: '15px' }} src={provider.logoUrl} alt={provider.name} />
              </Box>
              <Typography
                sx={{
                  alignContent: 'center',
                  ml: '10px',
                  color: !isEnabled && 'rgba(0, 0, 0, 0.54);',
                }}
              >
                {t('salesPoint:parcelLocker')}
              </Typography>
            </Box>

            <ItemDescription description={description} />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container sx={{ px: '20px', ml: '15px' }}>
            <Grid
              container
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              sx={{ justifyContent: 'center', width: '100%' }}
            >
              <Grid size="auto">
                <Autocomplete
                  disablePortal
                  value={selectedCity ?? null}
                  options={cities ?? []}
                  disabled={isLoading}
                  loading={isLoading}
                  getOptionLabel={(option) => option}
                  onChange={(_, option) => setSelectedCity(option)}
                  sx={{
                    width: { xs: '100%', md: '300px' },
                    '& fieldset': { borderRadius: '15px' },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('salesPoint:selectCity')}
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : (
                                params.InputProps.endAdornment
                              )}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size="grow">
                <Autocomplete
                  filterOptions={filterOptions}
                  sx={{
                    width: '100%',
                    '& fieldset': { borderRadius: '15px' },
                  }}
                  slotProps={{
                    listbox: {
                      style: { maxHeight: '300px' },
                    },
                  }}
                  value={selectedParcelLocker ?? null}
                  blurOnSelect
                  options={filteredParcelLockers ?? []}
                  disabled={isLoading || !selectedCity}
                  onChange={(_, option) => setSelectedParcelLocker(option)}
                  getOptionLabel={(option) => option.value.name}
                  renderInput={(params) => <TextField {...params} label={t('salesPoint:selectParcelLocker')} />}
                  renderOption={(props, parcelLocker) => {
                    const { key, ...optionProps } = props;
                    return (
                      <Box key={key} component="li" {...optionProps}>
                        <Grid
                          container
                          sx={{
                            justifyContent: 'space-between',
                            display: 'grid',
                            gridTemplateColumns: 'auto auto',
                            width: '100%',
                          }}
                        >
                          <Grid container sx={{ alignItems: 'center' }}>
                            <Typography sx={{ pr: '10px' }}>{parcelLocker.value.name}</Typography>
                            <Typography sx={{ fontSize: '12px', color: 'grey' }}>
                              {parcelLocker.value.address}
                            </Typography>
                          </Grid>
                          <Grid
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              fontSize: '30px',
                            }}
                          >
                            {parcelLocker.distanceInMeters && (
                              <Typography sx={{ color: '#1f4a8f' }}>
                                {(parcelLocker.distanceInMeters / 1000).toFixed(2)}
                                km
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
